/**
 * 답변 포맷 관리 — 구조·표시형식(STRUCTURE) 라이브러리 전용.
 * 카테고리/도구별 포맷 지정은 AI도구 화면에서 처리(이 화면은 포맷 정의 관리만).
 * (design-20260604-answer-tone-format-orthogonal §4 상세화)
 */
import { useEffect, useState } from 'react';
import './AiAnswerFormat.css';
import {
  useStyleConfig, FORMAT_PRESETS, FORMAT_PRESET_BY_KEY,
  FormatMock, FormatVizSample, StyleSaveBar, Field, formatKind, AiGeneratePanel, PalettePicker, PALETTE_ROLES,
} from './AiAnswerStyle.shared';
import { ConsoleScreenHead } from '../../../components/console-list/ConsoleScreenHead';

type Sel = { kind: 'format'; key: string } | null;
const OUTPUT_LABELS: Record<string, string> = { markdown: 'Markdown', html: 'HTML', data_table: '데이터 표' };
const BORDER_PRESETS = ['#e2e8f0', '#94a3b8', '#6366f1', '#16a34a', '#f59e0b', '#ef4444'];

// 보더 색상 선택 행 — 프리셋 + 팔레트1번 + 직접선택 + 없음.
function BorderRow({ label, value, palette0, onSet }: {
  label: string; value?: string; palette0?: string; onSet: (c: string | undefined) => void;
}) {
  return (
    <div className="aiv-bordersel">
      <span className="aiv-bordersel__lbl">{label}</span>
      <button type="button" className={`aiv-bordersel__none${!value ? ' is-sel' : ''}`} onClick={() => onSet(undefined)}>없음</button>
      {BORDER_PRESETS.map((c) => (
        <button key={c} type="button" title={c} style={{ background: c }}
          className={`aiv-bordersel__opt${value === c ? ' is-sel' : ''}`} onClick={() => onSet(c)} />
      ))}
      {palette0 && (
        <button type="button" className={`aiv-bordersel__none${value === palette0 ? ' is-sel' : ''}`} onClick={() => onSet(palette0)}>팔레트 1번색</button>
      )}
      <input type="color" className="aiv-bordersel__custom" value={value ?? '#e2e8f0'} title="직접 선택" onChange={(e) => onSet(e.target.value)} />
    </div>
  );
}

export function AiAnswerFormatSection() {
  const api = useStyleConfig();
  const { fmt, loading, update } = api;
  const [sel, setSel] = useState<Sel>(null);

  const formatKeys = fmt ? Object.keys(fmt.formats ?? {}) : [];

  useEffect(() => {
    if (!sel && formatKeys.length) setSel({ kind: 'format', key: formatKeys[0]! });
  }, [sel, formatKeys]);

  if (loading) return <div className="settings-loading">불러오는 중…</div>;
  if (!fmt) return <div className="settings-error">설정을 불러오지 못했습니다.</div>;

  // 이 포맷이 카테고리·도구에 매핑된 곳 수(참고).
  const fmtUsage = (key: string) => Object.values(fmt.style_by_tool ?? {}).filter((m) => m.format === key).length;

  return (
    <div className="aiaf-section svc-cat svc-cat--ai-fmt">
      <ConsoleScreenHead title="답변 포맷" kpi={`${formatKeys.length}개`} desc="구조·표시형식 포맷 라이브러리를 관리합니다." />
      <p className="settings-section-desc" style={{ margin: '0 0 14px' }}>
        AI 답변의 <b>구조·표시형식</b> 포맷(표/목록/리포트·흐름도·간트·파이 등)을 정의·관리합니다. 왼쪽에서 포맷을 선택하면 오른쪽에 편집·시각화·예제가 표시됩니다.
        카테고리·도구별 포맷 적용은 <b>AI 도구</b> 화면에서, 말투·어조는 <b>답변 톤</b> 메뉴에서 설정합니다.
      </p>

      {api.hasLegacy && (
        <div className="aiaf-migrate" role="status">
          기존(구버전) 설정이 있습니다. 톤·포맷 2축으로 자동 변환할 수 있습니다.
          <button type="button" className="settings-btn" onClick={() => api.importLegacy()}>기존 설정 가져오기</button>
        </div>
      )}

      {/* 공통 구조 */}
      <div className="aiaf-common">
        <div className="aiaf-common__title">공통 구조 (전 카테고리 공통)</div>
        <div className="aiaf-common__row">
          <label>목록 임계
            <input className="aiaf-input aiaf-input--num" type="number" min={1} max={50} value={fmt.format_hints?.list_threshold ?? ''} placeholder="3"
              onChange={(e) => update((f) => { f.format_hints = { ...f.format_hints, list_threshold: e.target.value === '' ? undefined : Number(e.target.value) }; })} />
          </label>
          <label>표 임계
            <input className="aiaf-input aiaf-input--num" type="number" min={1} max={50} value={fmt.format_hints?.table_threshold ?? ''} placeholder="4"
              onChange={(e) => update((f) => { f.format_hints = { ...f.format_hints, table_threshold: e.target.value === '' ? undefined : Number(e.target.value) }; })} />
          </label>
          <label className="aiaf-check">
            <input type="checkbox" checked={!!fmt.format_hints?.callout_for_warnings}
              onChange={(e) => update((f) => { f.format_hints = { ...f.format_hints, callout_for_warnings: e.target.checked }; })} /> 경고 콜아웃
          </label>
          <label className="aiaf-check">
            <input type="checkbox" checked={!!fmt.format_hints?.code_block_for_ids}
              onChange={(e) => update((f) => { f.format_hints = { ...f.format_hints, code_block_for_ids: e.target.checked }; })} /> ID 코드블록
          </label>
        </div>
      </div>

      <div className="aiaf-2pane">
        {/* 좌: 포맷 라이브러리 */}
        <div className="aiaf-2pane__list">
          <div className="aiaf-listgroup">
            <div className="aiaf-listgroup__title">포맷 라이브러리</div>
            {formatKeys.length === 0 && <p className="aiaf-empty">포맷이 없습니다. 아래에서 추가하세요.</p>}
            <div className="aiaf-picker" role="radiogroup" aria-label="포맷 라이브러리">
              {formatKeys.map((key) => {
                const f = fmt.formats![key]!;
                const on = sel?.kind === 'format' && sel.key === key;
                return (
                  <button key={key} type="button" role="radio" aria-checked={on}
                    className={`aiaf-pcard${on ? ' is-sel' : ''}`} onClick={() => setSel({ kind: 'format', key })}>
                    <span className="aiaf-pcard__mock"><FormatMock kind={formatKind(key, f)} /></span>
                    <span className="aiaf-pcard__label">{f.description || FORMAT_PRESET_BY_KEY[key]?.label || key}</span>
                    <span className="aiaf-pcard__desc">{OUTPUT_LABELS[f.output_format ?? 'markdown']} · {fmtUsage(key)}곳</span>
                  </button>
                );
              })}
            </div>
            <div className="aiaf-listgroup__add">
              <select className="aiaf-select aiaf-select--sm" value="" onChange={(e) => { if (e.target.value) { const k = api.addFormatFromPreset(e.target.value); setSel({ kind: 'format', key: k }); } e.currentTarget.value = ''; }}>
                <option value="">+ 프리셋에서</option>
                {FORMAT_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              <button type="button" className="settings-btn" onClick={() => { const k = api.addCustomFormat(); setSel({ kind: 'format', key: k }); }}>+ 직접</button>
            </div>
            <AiGeneratePanel kind="format" onAccept={(e) => { const k = api.addFormatEntry(e); setSel({ kind: 'format', key: k }); }} />
          </div>
        </div>

        {/* 우: 상세(편집·시각화·예제) */}
        <div className="aiaf-2pane__detail">
          {sel?.kind === 'format' && fmt.formats?.[sel.key] ? (() => {
            const key = sel.key; const f = fmt.formats![key]!;
            return (
              <div>
                <div className="aiaf-detail__head">
                  <span className="aiaf-detail__title">{f.description || key}</span>
                  <span className="aiaf-detail__key">{key}</span>
                  <button type="button" className="aiaf-detail__del" onClick={() => { api.removeFormat(key); setSel(null); }}>삭제</button>
                </div>
                <div className="aiaf-cat__row">
                  <Field label="이름">
                    <input className="aiaf-input aiaf-input--sm" value={f.description ?? ''} placeholder="포맷 이름"
                      onChange={(e) => api.editFormat(key, (x) => { x.description = e.target.value; })} />
                  </Field>
                  <Field label="출력 형식">
                    <select className="aiaf-select" value={f.output_format ?? 'markdown'}
                      onChange={(e) => api.editFormat(key, (x) => { x.output_format = e.target.value as 'markdown' | 'html' | 'data_table'; })}>
                      <option value="markdown">Markdown</option><option value="html">HTML</option>
                      <option value="data_table">데이터 표 (값 고정·결정적)</option>
                    </select>
                  </Field>
                </div>
                <label className="aiaf-instr">구조 지침 (한 줄에 하나)
                  <textarea className="aiaf-textarea" rows={Math.max(2, (f.instructions ?? []).length)} value={(f.instructions ?? []).join('\n')}
                    disabled={f.output_format === 'data_table'}
                    onChange={(e) => api.editFormat(key, (x) => { x.instructions = e.target.value.split('\n').map((s) => s.trimEnd()); })} />
                </label>
                <div className="aiaf-detail__sec">
                  <div className="aiaf-detail__sec-lbl">색상 팔레트</div>
                  <PalettePicker value={f.palette} onChange={(c) => api.editFormat(key, (x) => { if (c) x.palette = c; else delete x.palette; })} />
                  {/* 색 역할 범례 — 각 색이 어디에 매핑되는지 */}
                  {f.palette && f.palette.length > 0 && (
                    <ul className="aiv-pal-legend">
                      {PALETTE_ROLES.slice(0, f.palette.length).map((r, i) => (
                        <li key={i}><span className="aiv-pal-legend__sw" style={{ background: f.palette![i] }} /><b>{r.slot}</b> {r.label}</li>
                      ))}
                    </ul>
                  )}
                  {/* 외부·내부 보더 색상 (표·객체) */}
                  <BorderRow label="외부 보더 (바깥 테두리)" value={f.borderColor} palette0={f.palette?.[0]}
                    onSet={(c) => api.editFormat(key, (x) => { if (c) x.borderColor = c; else delete x.borderColor; })} />
                  <BorderRow label="내부 보더 (표·객체 셀 구분선)" value={f.innerBorderColor} palette0={f.palette?.[0]}
                    onSet={(c) => api.editFormat(key, (x) => { if (c) x.innerBorderColor = c; else delete x.innerBorderColor; })} />
                  <p className="aiaf-detail__key" style={{ marginTop: 8 }}>팔레트 색은 차트·다이어그램·HTML 강조에, 외부/내부 보더 색은 표·객체의 바깥 테두리·셀 구분선에 사용됩니다(채팅 답변에 반영).</p>
                </div>
                <div className="aiaf-detail__sec"><div className="aiaf-detail__sec-lbl">시각화</div><FormatVizSample formatKey={key} format={f} /></div>
                <p className="aiaf-detail__key">이 포맷을 사용하는 카테고리·도구: {fmtUsage(key)}곳 (AI 도구 화면에서 지정)</p>
              </div>
            );
          })() : (
            <div className="aiaf-detail__empty">왼쪽에서 포맷을 선택하세요.</div>
          )}
        </div>
      </div>

      <StyleSaveBar api={api} label="포맷 설정 저장" />
    </div>
  );
}
